import { useCallback, useRef, useState } from "react";
import { fetchSeriesWithSets, fetchSet } from "../lib/api";
import {
  hasLocalSet, hasLocalImage, writeImageFromUrl, cachedImageCountByQuality, flushManifestNow,
  hasLocalHQSet, markLocalHQSet,
} from "../lib/offlineStore";
import { remoteCardImageSrc, cardLocalImagePath, setAsset, setAssetLocalPath } from "../lib/images";

// Combien d'images télécharger en parallèle pendant la synchro manuelle.
// Volontairement modeste : contrairement à backend/download_images.py (qui
// tape le serveur lui-même), ceci tourne depuis l'appareil de l'utilisateur,
// sur sa propre connexion — pas besoin d'aller vite, juste de ne pas geler
// le reste de l'app pendant que ça tourne en fond.
const IMAGE_CONCURRENCY = 6;

// Synchronisation "vignettes + données" en arrière-plan : reprend la
// traversée déjà éprouvée côté serveur (series-with-sets -> chaque set ->
// ses cartes) mais exécutée depuis l'appareil, contre l'API live.
// Idempotente : un set déjà en cache local (hasLocalSet) est sauté, une
// image déjà en cache (hasLocalImage) aussi — relancer après une coupure
// reprend là où ça s'est arrêté, même principe que le script serveur.
//
// `quality` = "low" (par défaut, synchro globale automatique des vignettes)
// ou "high" (étape 3, à la demande depuis les Réglages : la fiche carte en
// grand). Le passage "low" saute un set déjà présent en cache (hasLocalSet,
// qui reflète le JSON) ; le passage "high" ne peut PAS réutiliser ce même
// critère (le JSON est déjà en cache pour la quasi-totalité des sets dès
// que la synchro "low" est passée) — il a son propre marqueur de complétion
// (hasLocalHQSet/markLocalHQSet), pour ne pas retaper le réseau sur ~220
// sets à chaque clic une fois la synchro HQ terminée.
export function useOfflineSync(quality = "low") {
  const [state, setState] = useState({
    running: false,
    syncedSets: 0,
    totalSets: 0,
    cachedImages: cachedImageCountByQuality(quality),
    totalImages: 0,
    currentSetName: null,
    error: null,
  });
  const cancelRef = useRef(false);

  const stop = useCallback(() => { cancelRef.current = true; }, []);

  const start = useCallback(async () => {
    cancelRef.current = false;
    setState(s => ({ ...s, running: true, error: null }));
    try {
      const series = await fetchSeriesWithSets();
      const allSets = series.flatMap(s => s.sets || []);
      const totalImages = allSets.reduce((n, s) => n + (s.cardCount?.total || 0), 0);
      setState(s => ({ ...s, totalSets: allSets.length, totalImages }));

      let syncedSets = 0;
      let cachedImages = cachedImageCountByQuality(quality);

      for (const setBrief of allSets) {
        if (cancelRef.current) break;
        setState(s => ({ ...s, currentSetName: setBrief.name }));

        const alreadyDone = quality === "high" ? hasLocalHQSet(setBrief.id) : hasLocalSet(setBrief.id);
        if (!alreadyDone) {
          // fetchSet() écrit déjà le JSON en cache lui-même (write-through,
          // cf. lib/api.js) : rien à faire ici pour la partie données.
          const fullSet = await fetchSet(setBrief.id).catch(() => null);
          if (fullSet) {
            if (quality === "low") await cacheSetAssets(fullSet);
            if (fullSet.cards) {
              cachedImages = await downloadSetImages(fullSet.cards, quality, cancelRef, cachedImages, (n) => {
                setState(s => ({ ...s, cachedImages: n }));
              });
            }
            if (quality === "high") markLocalHQSet(setBrief.id);
          }
        }
        syncedSets += 1;
        setState(s => ({ ...s, syncedSets }));
        // Point de coupure naturel : si l'appli est tuée juste après (ou
        // pendant) la synchro, les sets déjà traités restent visibles au
        // prochain lancement plutôt que de dépendre du seul debounce.
        flushManifestNow();
      }
    } catch (err) {
      setState(s => ({ ...s, error: err.message || "Erreur de synchronisation" }));
    } finally {
      setState(s => ({ ...s, running: false, currentSetName: null }));
    }
  }, [quality]);

  return { ...state, start, stop };
}

// Logo + symbole du set : mêmes assets que ceux affichés sur la tuile
// d'accueil et l'en-tête de la fiche set (SetCard/SetDetailView, via
// useSetAsset) — sans ça ils restaient invisibles hors ligne alors que les
// cartes, elles, étaient bien synchronisées. Pas de variante de qualité :
// couverts une seule fois par le passage "low".
async function cacheSetAssets(set) {
  const jobs = [];
  if (set.logo) {
    const url = setAsset(set.logo);
    jobs.push(writeImageFromUrl(setAssetLocalPath(set.id, "logo", set.logo), url));
  }
  if (set.symbol) {
    const url = setAsset(set.symbol);
    jobs.push(writeImageFromUrl(setAssetLocalPath(set.id, "symbol", set.symbol), url));
  }
  await Promise.all(jobs);
}

async function downloadSetImages(cards, quality, cancelRef, startCount, onProgress) {
  let count = startCount;
  let i = 0;
  const worker = async () => {
    while (i < cards.length) {
      if (cancelRef.current) return;
      const card = cards[i++];
      if (!card.image) continue;
      const path = cardLocalImagePath(card, quality);
      // Cartes `image_full` (Pokécardex/EN) : même fichier quelle que soit
      // la qualité demandée (cardLocalImagePath renvoie le même chemin) —
      // déjà récupéré par le passage "low", donc no-op ici, pas de double
      // téléchargement.
      if (hasLocalImage(path)) continue;
      const ok = await writeImageFromUrl(path, remoteCardImageSrc(card, quality));
      if (ok) { count += 1; onProgress(count); }
    }
  };
  await Promise.all(Array.from({ length: IMAGE_CONCURRENCY }, worker));
  return count;
}
